import React from 'react';
import classNames from 'classnames';
import { NovaConversationSummary } from '@/types';
import { novaThemes, NovaThemeName } from './novaTheme';
import { NovaChevronRightIcon, NovaCloseIcon } from './novaIcons';

interface NovaHistorySidebarProps {
  conversations: NovaConversationSummary[];
  loading?: boolean;
  activeConversationId?: string;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string) => void;
  theme: NovaThemeName;
  /** Terse single-line rows (title only, no preview/delete) for a persistent rail, vs. the full two-line view. */
  compact?: boolean;
  /** Caps the rows shown, pairing with a trailing "View all" row that clears the cap. Ignored unless `compact`. */
  limit?: number;
  /** Instead of a fixed `limit`, fill the parent's height: show as many rows as fit, with
   * "View all" pinned to the bottom. The parent must give this a bounded height. Ignored unless `compact`. */
  fill?: boolean;
}

// Keyboard focus ring for the rows — drawn inset so the list's overflow clipping can't hide it.
const FOCUS_RING_CLASS =
  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--nova-focus-ring)]';

// Fallback before the first row has rendered: py-[9px] + 20px line + the 1px gap-px.
const COMPACT_ROW_HEIGHT = 39;

const NovaHistorySidebar: React.FC<NovaHistorySidebarProps> = ({
  conversations,
  loading,
  activeConversationId,
  onSelect,
  onDelete,
  theme,
  compact,
  limit,
  fill,
}) => {
  const surfaces = novaThemes[theme];
  const [expanded, setExpanded] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);
  // How many rows fit in the list area when `fill` is on — re-measured whenever the
  // rail resizes (window height changes, the "View all" row appearing, etc.).
  const [fitCount, setFitCount] = React.useState<number>();
  const hasConversations = conversations.length > 0;

  React.useEffect(() => {
    const list = listRef.current;
    if (!compact || !fill || !list) return;
    const measure = () => {
      const firstRow = list.firstElementChild as HTMLElement | null;
      const rowHeight = firstRow ? firstRow.offsetHeight + 1 : COMPACT_ROW_HEIGHT;
      setFitCount(Math.max(1, Math.floor((list.clientHeight + 1) / rowHeight)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [compact, fill, loading, hasConversations]);

  if (loading) {
    return (
      <div className="flex-1 p-3 text-sm" style={{ color: surfaces.ink.placeholder }}>
        Loading......
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex-1 p-3 text-sm" style={{ color: surfaces.ink.placeholder }}>
        No previous chats yet.
      </div>
    );
  }

  if (compact) {
    const cap = fill ? fitCount : limit;
    const capped = cap && !expanded ? conversations.slice(0, cap) : conversations;
    // Real <button>s (not clickable divs) so every row is reachable with Tab and activates
    // with Enter/Space.
    const rows = capped.map((conversation) => (
      <button
        key={conversation.conversationId}
        type="button"
        className={classNames(
          'block w-full shrink-0 cursor-pointer truncate rounded-[9px] border-0 bg-transparent px-3 py-[9px] text-left text-sm',
          FOCUS_RING_CLASS,
        )}
        style={
          {
            color: surfaces.ink.secondary,
            background: conversation.conversationId === activeConversationId ? surfaces.surfaces.activeRow : undefined,
            '--nova-focus-ring': surfaces.ink.heading,
          } as React.CSSProperties
        }
        aria-current={conversation.conversationId === activeConversationId ? 'true' : undefined}
        onClick={() => onSelect(conversation.conversationId)}
      >
        {conversation.title}
      </button>
    ));
    const toggle = cap && conversations.length > cap && (
      <button
        type="button"
        className={classNames(
          'flex w-full shrink-0 cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-[9px] text-left text-sm',
          FOCUS_RING_CLASS,
        )}
        style={{ color: surfaces.ink.heading, '--nova-focus-ring': surfaces.ink.heading } as React.CSSProperties}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? 'Show less' : 'View all'}
        <NovaChevronRightIcon className={classNames('h-3.5 w-3.5 transition-transform', expanded && '-rotate-90')} />
      </button>
    );

    if (fill) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <div
            ref={listRef}
            className={classNames('flex min-h-0 flex-1 flex-col gap-px', expanded ? 'overflow-y-auto' : 'overflow-hidden')}
            data-lenis-prevent
          >
            {rows}
          </div>
          {toggle}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-px">
        {rows}
        {toggle}
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-0.5 overflow-y-auto" data-lenis-prevent>
      {conversations.map((conversation) => (
        // The row is a plain container: selecting and deleting are two sibling buttons (a
        // button can't nest another), so both are reachable with Tab. The delete button is
        // hidden until hover, and now also revealed when it or the row has keyboard focus.
        <div
          key={conversation.conversationId}
          className="group flex min-h-[58px] items-center gap-2 rounded-xl hover:bg-[var(--nova-row-hover)]"
          style={
            {
              background: conversation.conversationId === activeConversationId ? surfaces.surfaces.activeRow : undefined,
              '--nova-row-hover': surfaces.surfaces.field,
              '--nova-delete-hover': surfaces.surfaces.activeRow,
              '--nova-focus-ring': surfaces.ink.heading,
            } as React.CSSProperties
          }
        >
          <button
            type="button"
            className={classNames(
              'flex min-w-0 flex-1 cursor-pointer flex-col justify-center gap-0.5 self-stretch rounded-xl border-0 bg-transparent py-2.5 pr-0 pl-3 text-left',
              FOCUS_RING_CLASS,
            )}
            aria-current={conversation.conversationId === activeConversationId ? 'true' : undefined}
            onClick={() => onSelect(conversation.conversationId)}
          >
            <span className="block truncate text-sm font-medium" style={{ color: surfaces.ink.heading }}>
              {conversation.title}
            </span>
            {conversation.lastMessagePreview && (
              <span className="block truncate text-xs" style={{ color: surfaces.ink.secondary }}>
                {conversation.lastMessagePreview}
              </span>
            )}
          </button>
          <button
            type="button"
            aria-label={`Delete conversation: ${conversation.title}`}
            className={classNames(
              'mr-3 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-[var(--nova-delete-hover)]',
              FOCUS_RING_CLASS,
            )}
            style={{ color: surfaces.ink.chromeIcon }}
            onClick={() => onDelete(conversation.conversationId)}
          >
            <NovaCloseIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default NovaHistorySidebar;
